/* ============================================================
 * Radar visual configuration.
 *
 * Central source of truth for the radar's visual styling: stroke
 * weights, opacity, dash patterns, and selected/alert-state settings.
 * Values are semantic (what a mark means in the hierarchy), not tied to
 * any particular element, so the rendering code imports these instead of
 * embedding magic numbers. Changing the radar's look should only require
 * edits here. These constants describe styling only — they do not affect
 * radar geometry, calculations, or interaction behavior.
 * ============================================================
 */

/*
 * Stroke weights in CSS px, defining the radar's visual hierarchy from
 * least to most prominent (scaled by devicePixelRatio at draw time, see
 * Simulator.strokeWidth):
 *
 *   auxiliary 0.5  <  reference 0.75  <  overlay 1  <  object 1.25
 *     <  vector 1.5  <  focus 2  <  alert 2.5
 *
 * Each tier MUST stay strictly heavier than the one below it: that ordering
 * is what keeps important marks legible. Background reference geometry and
 * projections recede; target symbols and vectors sit above them; ownship and
 * the selected target read as focus; warning targets are heaviest. Collapsing
 * or reordering these values flattens the hierarchy and buries the objects
 * that matter — verifyStrokeHierarchy() (below) guards the invariant.
 */
export const referenceStroke = 0.75; // range rings, bearing ticks, static grid geometry
export const auxiliaryStroke = 0.5;  // dotted projection lines, trails, secondary guide lines
export const overlayStroke = 1;      // analytical overlays — selected-target CPA / closest-point line
export const objectStroke = 1.25;    // target boxes / symbols
export const vectorStroke = 1.5;     // target course/speed (and wind) vectors
export const focusStroke = 2;        // ownship, selected target outline + vector
export const alertStroke = 2.5;      // collision-risk / warning-state targets

/*
 * Semantic token → stroke weight, for strokeWidth(token) lookups. Keys are
 * the hierarchy tiers; values reference the named strokes above.
 */
export const strokeWeights = {
    reference: referenceStroke,
    auxiliary: auxiliaryStroke,
    overlay: overlayStroke,
    object: objectStroke,
    vector: vectorStroke,
    focus: focusStroke,
    alert: alertStroke,
};

/*
 * Canonical hierarchy order, faintest → heaviest. Kept explicit (rather than
 * relying on object key order) so the intended ranking is unambiguous and
 * machine-checkable.
 */
export const strokeHierarchy = ['auxiliary', 'reference', 'overlay', 'object', 'vector', 'focus', 'alert'];

/*
 * Invariant guard for the stroke hierarchy: returns null when every tier is
 * strictly heavier than the previous one, or a descriptive message when the
 * ranking has been flattened or reordered. Pure and dependency-free, so it can
 * be asserted from a test or CI — e.g. assert(verifyStrokeHierarchy() === null)
 * — to stop a future change from accidentally flattening the radar.
 */
export function verifyStrokeHierarchy() {
    for (let i = 1; i < strokeHierarchy.length; i++) {
        const lighter = strokeHierarchy[i - 1];
        const heavier = strokeHierarchy[i];
        if (!(strokeWeights[heavier] > strokeWeights[lighter])) {
            return `radar stroke hierarchy flattened: '${heavier}' (${strokeWeights[heavier]}) must be heavier than '${lighter}' (${strokeWeights[lighter]})`;
        }
    }
    return null;
}

/* Opacity (alpha) values for de-emphasized layers. */
// Range rings / bearing ticks: dims the 0.5-alpha faint green to ~0.3 effective,
// so background reference geometry does not compete with targets/vectors.
export const referenceGridAlpha = 0.6;
// Non-selected target course/speed vectors recede so dense scenes stay readable.
export const nonSelectedVectorAlpha = 0.75;
// Dotted projection/guide lines (CPA, bearing) sit below the active-vector tier.
export const projectionLineAlpha = 0.6;

/* Dash patterns ([] = solid). */
export const dashPatterns = {
    solid: [],
    bearingTick: [4, 4],     // non-cardinal bearing ticks
    cpaLine: [2, 3],         // selected-target CPA / closest-point line
    bearingLine: [2, 4],     // selected-target bearing guide line
    relativeMotion: [5, 5],  // relative-motion vectors
    wind: [5, 5],            // relative-wind vector
};

/* Selected / hover state. */
// Radius of the selection ring as a multiple of the target symbol size. The
// selected ring is deliberately larger than the subtle hover ring.
export const selectedRingRadiusScale = 1.35;
export const hoverRingRadiusScale = 1.2;

/* Alert / warning state. */
// Second (non-width) alert channel drawn beside warning targets, so alert
// state never relies on stroke width alone.
export const alertGlyph = '!';
